import { useState, useRef, useEffect } from 'react';
import {
  FileText, Download, Printer, Pencil, Copy, Trash2, ChevronDown,
} from 'lucide-react';

// ── Dropdown menu ─────────────────────────────────────────────────────────────
function Dropdown({ trigger, items, align = 'right' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <div onClick={() => setOpen(!open)}>{trigger}</div>
      {open && (
        <div
          className={`absolute top-full mt-1 z-50 min-w-[180px] bg-white border border-[#e5e4df] rounded shadow-lg py-1 ${align === 'right' ? 'right-0' : 'left-0'}`}
        >
          {items.map((item, idx) =>
            item.type === 'divider' ? (
              <div key={idx} className="my-1 border-t border-[#e5e4df]" />
            ) : (
              <button
                key={idx}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#2c2c2c] hover:bg-[#f3f2ef] transition-colors text-left"
                onClick={() => {
                  setOpen(false);
                  item.onClick?.();
                }}
              >
                {item.icon && <item.icon className="w-4 h-4 text-[#6b7280]" />}
                {item.label}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ── Export ▾ dropdown (toolbar variant) ────────────────────────────────────────
export function ExportDropdown({ onPdfPreview, onPdfDownload, onPrint }) {
  const trigger = (
    <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#1a1a2e] bg-white border border-[#e5e4df] rounded hover:bg-[#f3f2ef] transition-colors">
      <Download className="w-4 h-4" />
      Export
      <ChevronDown className="w-3.5 h-3.5 text-[#6b7280]" />
    </button>
  );

  return (
    <Dropdown
      trigger={trigger}
      items={[
        { icon: FileText, label: 'PDF Preview', onClick: onPdfPreview },
        { icon: Download, label: 'Export PDF', onClick: onPdfDownload },
        { icon: Printer, label: 'Print', onClick: onPrint },
      ]}
    />
  );
}

// ── Actions ▾ dropdown (page-level variant with export submenu) ─────────────
export function ActionsDropdown({ onEdit, onDuplicate, onDelete, exportItems }) {
  const trigger = (
    <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#1a1a2e] bg-white border border-[#e5e4df] rounded hover:bg-[#f3f2ef] transition-colors">
      <ChevronDown className="w-4 h-4 text-[#6b7280]" />
      Actions
    </button>
  );

  const items = [];

  if (onEdit) items.push({ icon: Pencil, label: 'Edit', onClick: onEdit });
  if (onDuplicate) items.push({ icon: Copy, label: 'Duplicate', onClick: onDuplicate });
  if (onDelete) items.push({ icon: Trash2, label: 'Delete', onClick: onDelete });

  if (exportItems && exportItems.length > 0) {
    items.push({ type: 'divider' });
    // Export as sub-label
    items.push({ icon: Download, label: '▸ Export', onClick: null }); // visual only
    exportItems.forEach(item => {
      items.push({
        icon: item.icon,
        label: `   ${item.label}`,
        onClick: item.onClick,
      });
    });
  }

  return <Dropdown trigger={trigger} items={items} />;
}

export default Dropdown;
